import { Component, inject, input, OnInit, signal } from '@angular/core';
import { IMessage } from '../../domain/message';
import { AudioPlayTask, AudioTaskState } from '../../audio/audio-play-task';
import { NgClass } from '@angular/common';
import { AudioTaskStore } from '../../store/audio-task.store';
import { combineLatestWith, firstValueFrom } from 'rxjs';
import { AudioPlaybackTaskFactory } from '../../audio/playback/audio-playback.task-factory';
import { AttachmentService } from '../../services/attachment.service';
import { Clipboard } from '@capacitor/clipboard';
import { ToastrService } from 'ngx-toastr';

export type ActionType = 'play' | 'pause' | 'resume' | 'unknown';

@Component({
    selector: 'app-message-control',
    imports: [NgClass],
    templateUrl: './message-control.html',
    styleUrl: './message-control.scss',
})
export class MessageControl implements OnInit {
    private readonly _audioTaskStore = inject(AudioTaskStore);
    private readonly _audioPlaybackTaskFactory = inject(AudioPlaybackTaskFactory);
    private readonly _attachmentService = inject(AttachmentService);
    private readonly _toastr = inject(ToastrService);

    private _currentAudioTaskSnapshot: AudioPlayTask | null = null;

    public message = input.required<IMessage>();
    public actionType = signal<ActionType>('unknown');
    public loading = signal<boolean>(false);

    constructor() {}

    public ngOnInit(): void {
        this._audioTaskStore.currentAudioTask
            .pipe(
                combineLatestWith(
                    this._audioTaskStore.currentAudioTaskState,
                    this._audioTaskStore.currentMessage,
                ),
            )
            .subscribe(([audioTask, audioTaskState, message]) => {
                this.actionType.set(this.calcActionType(audioTask, audioTaskState, message));
                this._currentAudioTaskSnapshot = audioTask;
            });
    }

    protected performAction(currentActionType: ActionType) {
        if (currentActionType === 'play') {
            const message = this.message();
            const source =
                message.audioBytes().length > 0
                    ? Promise.resolve({ audioBytes: message.audioBytes() })
                    : this.fetchAudioBytes(message);

            this.executeAudioTask(
                source
                    .then(actualSource => {
                        this._audioTaskStore.setCurrentMessage(message);
                        return this._audioTaskStore.setCurrentTask(
                            this._audioPlaybackTaskFactory.createAudioPlaybackTask(actualSource),
                        );
                    }),
                'An error occurred while playing audio',
            );

            return;
        }

        if (currentActionType === 'pause') {
            if (this._currentAudioTaskSnapshot !== null) {
                this.executeAudioTask(
                    this._currentAudioTaskSnapshot.pause(),
                    'An error occurred while pausing audio task');
            }

            return;
        }

        if (currentActionType === 'resume') {
            if (this._currentAudioTaskSnapshot !== null) {
                this.executeAudioTask(
                    this._currentAudioTaskSnapshot.resume(),
                    'An error occurred while resuming audio task',
                );
            }

            return;
        }
    }

    private executeAudioTask(task: Promise<unknown>, errorMessage: string) {
        this.loading.set(true);

        task
            .catch(error => {
                console.error(errorMessage, error);
                this._toastr.error(errorMessage);
            })
            .finally(() => {
                this.loading.set(false);
            });
    }

    private async fetchAudioBytes(message: IMessage) {
        for (const attachment of message.attachments ?? []) {
            if (attachment.contentType?.startsWith('audio')) {
                const audioBytesBlob = await firstValueFrom(
                    this._attachmentService.get(attachment.attachmentId),
                );

                const arrayBuffer = await audioBytesBlob.arrayBuffer();
                const byteArray = new Uint8Array(arrayBuffer);

                return { audioBytes: byteArray };
            }
        }

        throw new Error('No audio found');
    }

    private calcActionType(audioTask: AudioPlayTask | null, state: AudioTaskState, message: IMessage | null): ActionType {
        const currentMessage = this.message();
        if (audioTask === null || message === null || message !== currentMessage || state === 'complete') {
            return 'play';
        }

        if (state === 'paused') {
            return 'resume';
        }

        if (state === 'playing') {
            return 'pause';
        }

        return 'unknown';
    }

    protected async copy() {
        const textToCopy = this.message().text() + '';
        await Clipboard.write({string: textToCopy });
        this._toastr.success('Message is copied.');
    }
}
